import { useCallback, useEffect, useState } from 'react';

import { api, type AutoLista, type AutoSor } from '../api.ts';
import { ft } from '../formazas.ts';
import { Mezo, SzamMezo } from '../reszek/Mezo.tsx';

/**
 * Autók. (Megbízás 2.0 / 5.8)
 *
 * Az 1.0-ban a rendszám a sofőr során lógott. Itt önálló nyilvántartás lett
 * belőle, mert a **műszaki, a biztosítás és a szerviz lejáratát figyelni kell** —
 * ezek később az Áttekintésen is megjelennek teendőként.
 *
 * A készlet továbbra is a sofőrhöz tartozik, nem az autóhoz.
 */

/** Hány nap múlva esedékes? Negatív, ha már lejárt. */
function napokMulva(datum: string | null): number | null {
  if (!datum) return null;
  const d = Date.parse(datum);
  if (Number.isNaN(d)) return null;
  const ma = new Date();
  const maNap = Date.parse(
    `${ma.getFullYear()}-${String(ma.getMonth() + 1).padStart(2, '0')}-${String(
      ma.getDate(),
    ).padStart(2, '0')}`,
  );
  return Math.round((d - maNap) / 86_400_000);
}

/** A dátumcella osztálya és alszövege. Piros = lejárt, sárga = hamarosan. */
function lejaratAllapot(datum: string | null, hatar: number) {
  const nap = napokMulva(datum);
  if (nap === null) return { osztaly: '', szoveg: '' };
  if (nap < 0) return { osztaly: 'lejart', szoveg: `${Math.abs(nap)} napja lejárt` };
  if (nap <= hatar) return { osztaly: 'lejar-hamarosan', szoveg: `${nap} nap múlva` };
  if (nap <= 60) return { osztaly: '', szoveg: `${nap} nap múlva` };
  return { osztaly: '', szoveg: '' };
}

export function Autok() {
  const [lista, setLista] = useState<AutoLista | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [ujNyitva, setUjNyitva] = useState(false);

  const betolt = useCallback(async () => {
    try {
      setLista(await api.get<AutoLista>('/api/auto'));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni az autókat.');
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /** Egy mező mentése. A dátumoknál újratöltünk, mert a figyelmeztetések változnak. */
  const ment = useCallback(
    async (id: number, mezo: string, ertek: unknown) => {
      const frissitett = await api.patch<AutoSor>(`/api/auto/${id}/mezo/${mezo}`, { ertek });
      setLista((elozo) =>
        elozo
          ? { ...elozo, sorok: elozo.sorok.map((a) => (a.id === id ? frissitett : a)) }
          : elozo,
      );
      if (mezo.endsWith('_lejar') || mezo === 'aktiv') await betolt();
    },
    [betolt],
  );

  async function torol(a: AutoSor) {
    if (!confirm(`Törlöd a ${a.rendszam} autót?`)) return;
    try {
      await api.torol(`/api/auto/${a.id}`);
      setUzenet(`${a.rendszam} törölve.`);
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A törlés nem sikerült.');
    }
  }

  const sorok = lista?.sorok ?? [];
  const o = lista?.osszesites;
  const hatar = lista?.figyelmeztetes_nap ?? 30;

  return (
    <>
      <div className="cards">
        <div className="card">
          <em>Autó</em>
          <b>{o?.aktiv ?? 0}</b>
          <span>{o ? `${o.osszes} nyilvántartva` : ''}</span>
        </div>
        <div className="card">
          <em>Össz kapacitás</em>
          <b>{ft(o?.kapacitas ?? 0)}</b>
          <span>liter</span>
        </div>
        <div className={`card${(o?.lejart ?? 0) > 0 ? ' warn2' : ''}`}>
          <em>Lejárt okmány</em>
          <b>{o?.lejart ?? 0}</b>
          <span>műszaki, biztosítás, szerviz</span>
        </div>
        <div className={`card${(o?.hamarosan ?? 0) > 0 ? ' warn2' : ''}`}>
          <em>{hatar} napon belül lejár</em>
          <b>{o?.hamarosan ?? 0}</b>
          <span>okmány</span>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      <div className="actbar">
        <button className="btn sm" onClick={() => setUjNyitva((v) => !v)}>
          {ujNyitva ? '× Mégsem' : '+ Új autó'}
        </button>
        <span className="hint">
          A dátumok alakja <code>ÉÉÉÉ-HH-NN</code>. A lejáró okmányokra később az Áttekintés
          is figyelmeztet.
        </span>
      </div>

      {ujNyitva && (
        <UjAuto
          onKesz={() => {
            setUjNyitva(false);
            void betolt();
          }}
          onMegse={() => setUjNyitva(false)}
        />
      )}

      <div className="matrix">
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Rendszám</th>
              <th>Típus</th>
              <th className="n" style={{ width: 90 }}>
                Kapacitás
              </th>
              <th style={{ width: 150 }}>Sofőr</th>
              <th className="n" style={{ width: 115 }}>
                Műszaki
              </th>
              <th className="n" style={{ width: 115 }}>
                Biztosítás
              </th>
              <th className="n" style={{ width: 115 }}>
                Szerviz
              </th>
              <th>Megjegyzés</th>
              <th style={{ width: 55 }}>Aktív</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((a) => (
              <tr key={a.id} className={a.aktiv ? undefined : 'gyr'}>
                <td className="nm3">
                  <Mezo ertek={a.rendszam} onMent={(v) => ment(a.id, 'rendszam', v)} />
                </td>
                <td>
                  <Mezo ertek={a.tipus} onMent={(v) => ment(a.id, 'tipus', v)} />
                </td>
                <td>
                  <SzamMezo ertek={a.kapacitas} onMent={(v) => ment(a.id, 'kapacitas', v)} />
                </td>
                <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                  {a.soforok.length > 0 ? (
                    a.soforok.join(', ')
                  ) : (
                    <span className="only">nincs hozzárendelve</span>
                  )}
                </td>
                {(['muszaki_lejar', 'biztositas_lejar', 'szerviz_lejar'] as const).map((k) => {
                  const all = lejaratAllapot(a[k], hatar);
                  return (
                    <td key={k}>
                      <Mezo
                        ertek={a[k]}
                        onMent={(v) => ment(a.id, k, v)}
                        osztaly={`c ${all.osztaly}`}
                      />
                      {all.szoveg && (
                        <div className={`lejarat-alszoveg ${all.osztaly}`}>{all.szoveg}</div>
                      )}
                    </td>
                  );
                })}
                <td>
                  <Mezo ertek={a.megjegyzes} onMent={(v) => ment(a.id, 'megjegyzes', v)} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={a.aktiv}
                    onChange={(e) => void ment(a.id, 'aktiv', e.target.checked)}
                  />
                </td>
                <td className="act">
                  <button
                    className="xb"
                    title={
                      a.torolheto
                        ? 'Törlés'
                        : `${a.soforok.join(', ')} ehhez az autóhoz van rendelve`
                    }
                    onClick={() => void torol(a)}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Még nincs autó felvéve. A sofőrök rendszámából a program magától létrehozta
                  volna őket — ha itt üres, még sofőr sincs.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(lista?.lejaro_okmanyok.length ?? 0) > 0 && (
        <>
          <p className="lbl" style={{ marginTop: 16 }}>
            Lejárt vagy hamarosan lejáró okmányok
          </p>
          <div className="matrix">
            <table className="tt2">
              <tbody>
                {lista!.lejaro_okmanyok.map((l) => (
                  <tr key={`${l.auto_id}-${l.fajta}`}>
                    <td className="nm3" style={{ width: 110 }}>
                      <b>{l.rendszam}</b>
                    </td>
                    <td style={{ padding: '7px 10px', width: 110 }}>{l.fajta_nev}</td>
                    <td className="calc2" style={{ width: 110 }}>
                      {l.datum}
                    </td>
                    <td
                      style={{ padding: '7px 10px' }}
                      className={l.nap < 0 ? 'lejart' : 'lejar-hamarosan'}
                    >
                      {l.nap < 0 ? `${Math.abs(l.nap)} napja lejárt` : `${l.nap} nap múlva jár le`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function UjAuto({ onKesz, onMegse }: { onKesz: () => void; onMegse: () => void }) {
  const [rendszam, setRendszam] = useState('');
  const [tipus, setTipus] = useState('');
  const [kapacitas, setKapacitas] = useState('');
  const [muszaki, setMuszaki] = useState('');
  const [biztositas, setBiztositas] = useState('');
  const [szerviz, setSzerviz] = useState('');
  const [hiba, setHiba] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);

  async function felvesz() {
    setMunkaban(true);
    setHiba(null);
    try {
      await api.post('/api/auto', {
        rendszam,
        tipus,
        kapacitas,
        muszaki_lejar: muszaki,
        biztositas_lejar: biztositas,
        szerviz_lejar: szerviz,
      });
      onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A felvétel nem sikerült.');
      setMunkaban(false);
    }
  }

  return (
    <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
      <h4>Új autó</h4>
      {hiba && <div className="uzenet bad">{hiba}</div>}
      <div className="qgrid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <label>
          Rendszám
          <input value={rendszam} onChange={(e) => setRendszam(e.target.value)} placeholder="ABC-123" />
        </label>
        <label>
          Típus
          <input
            value={tipus}
            onChange={(e) => setTipus(e.target.value)}
            placeholder="Mercedes Sprinter"
            style={{ textAlign: 'left', fontFamily: 'inherit' }}
          />
        </label>
        <label>
          Kapacitás (liter)
          <input value={kapacitas} onChange={(e) => setKapacitas(e.target.value)} />
        </label>
      </div>
      <div className="qgrid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <label>
          Műszaki lejár
          <input value={muszaki} onChange={(e) => setMuszaki(e.target.value)} placeholder="ÉÉÉÉ-HH-NN" />
        </label>
        <label>
          Biztosítás lejár
          <input
            value={biztositas}
            onChange={(e) => setBiztositas(e.target.value)}
            placeholder="ÉÉÉÉ-HH-NN"
          />
        </label>
        <label>
          Szerviz esedékes
          <input value={szerviz} onChange={(e) => setSzerviz(e.target.value)} placeholder="ÉÉÉÉ-HH-NN" />
        </label>
      </div>
      <div className="btns">
        <button className="btn pri sm" disabled={munkaban || !rendszam} onClick={() => void felvesz()}>
          Felvétel
        </button>
        <button className="btn sm" onClick={onMegse}>
          Mégsem
        </button>
      </div>
    </div>
  );
}
